import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { useForm, SubmitHandler, SubmitErrorHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import "../../assets/css/majorybl/pages/profile.scss";

import { IUserProfileData } from "../../types/forms";
import { useLoaderData } from "react-router";
import { PHONE_REGEXP } from "../../lib/globals";
import { getUser, updateUser } from "../../lib/api";
import { displayFormAlert, getUserFromLocalStorage } from "../../lib/utils";
import { IUser } from "../../types/api";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { alertActions } from "../../features/alert/alertSlice";

export const loader = async ({ params }: any) => {
    let user;

    if (params.userId !== undefined) {
        user = await getUser(params.userId);
    } else {
        const localUser = getUserFromLocalStorage();
        user = localUser && (await getUser(localUser.id));
    }

    return user;
};

const Profile = (): React.JSX.Element => {
    const { user } = useLoaderData() as { user: IUser };
    const dispatch = useAppDispatch();

    const profileFormSchema = yup.object().shape(
        {
            id: yup.number().required(),
            firstName: yup.string().required("First Name is required"),
            lastName: yup.string().required("Last Name is required"),
            email: yup
                .string()
                .required("Email is required")
                .email("Email is invalid"),
            password: yup.string().when("password", {
                is: "",
                then: schema =>
                    schema
                        .transform((value, originalValue) => {
                            if (!value) {
                                return null;
                            }
                            return originalValue;
                        })
                        .nullable()
                        .optional(),
                otherwise: schema =>
                    schema.min(
                        8,
                        "Password length should be at least 8 characters",
                    ),
            }),
            confirmPassword: yup
                .string()
                .when(["password", "confirmPassword"], {
                    is: "",
                    then: schema =>
                        schema
                            .transform((value, originalValue) => {
                                if (!value) {
                                    return null;
                                }
                                return originalValue;
                            })
                            .nullable()
                            .optional(),
                    otherwise: schema =>
                        schema
                            .min(
                                8,
                                "Password length should be at least 8 characters",
                            )
                            .oneOf(
                                [yup.ref("password")],
                                "Passwords do not match",
                            ),
                }),
            phone: yup
                .string()
                .required("Phone number is required")
                .matches(PHONE_REGEXP, "Phone number is not valid"),
        },
        [
            ["password", "password"],
            ["confirmPassword", "password"],
            ["confirmPassword", "confirmPassword"],
        ],
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IUserProfileData>({
        mode: "onChange",
        resolver: yupResolver(profileFormSchema),
        defaultValues: {
            id: user.id,
            email: user.email,
            firstName: user.meta.firstName,
            lastName: user.meta.lastName,
            phone: user.meta.phoneNumbers.primary,
            password: "",
            confirmPassword: "",
        },
    });

    const onSubmit: SubmitHandler<IUserProfileData> = async data => {
        await updateUser(data)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Profile updated successfully",
                        dismissible: true,
                    }),
                );
            })
            .catch(e =>
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem updating your profile.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                ),
            );
    };

    const onError: SubmitErrorHandler<IUserProfileData> = (): void => {};

    return (
        <div data-testid="profileContent">
            <Container className="py-5 profile">
                <Row>
                    <Col md={8}>
                        <Form
                            onSubmit={handleSubmit(onSubmit, onError)}
                            id="profileForm"
                        >
                            <Row>
                                <Col>
                                    <h2>Edit Account</h2>
                                    <p>
                                        <b>Note:</b> All fields are required
                                    </p>
                                </Col>
                            </Row>
                            {/*
                        //! if admin, select user to edit
                        <Row>
                        <Col>
                            <Form.Select
                                {...register("contactType", { required: true })}
                                id="contactType"
                                className="mb-4"
                                defaultValue=""
                                aria-invalid={
                                    errors.contactType ? "true" : "false"
                                }
                            >
                                <option disabled value="">
                                    Select Option
                                </option>
                                <option value="existing">
                                    I'm already part of the league and have a
                                    question
                                </option>
                                <option value="new">
                                    I'm considering joining the league and have
                                    a question
                                </option>
                            </Form.Select>
                            {errors.contactType &&
                                displayFormAlert({level: "danger", text: errors.contactType.message as string,})}
                        </Col>
                    </Row> */}
                            <Row>
                                <Col>
                                    <h4>Name</h4>
                                </Col>
                            </Row>
                            <Form.Group controlId="firstName" as={Row}>
                                <Form.Label column sm={3}>
                                    First Name
                                </Form.Label>
                                <Col sm={9}>
                                    <Form.Control
                                        type="text"
                                        {...register("firstName", {
                                            required: "First Name is required",
                                        })}
                                        aria-invalid={
                                            errors.firstName ? "true" : "false"
                                        }
                                    />
                                    {errors.firstName &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.firstName
                                                .message as string,
                                        })}
                                </Col>
                            </Form.Group>
                            <Form.Group controlId="lastName" as={Row}>
                                <Form.Label column sm={3}>
                                    Last Name
                                </Form.Label>
                                <Col sm={9}>
                                    <Form.Control
                                        type="text"
                                        {...register("lastName", {
                                            required: "Last Name is required",
                                        })}
                                        aria-invalid={
                                            errors.lastName ? "true" : "false"
                                        }
                                    />
                                    {errors.lastName &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.lastName
                                                .message as string,
                                        })}
                                </Col>
                            </Form.Group>
                            <Row>
                                <Col>
                                    <h4>Contact Info</h4>
                                </Col>
                            </Row>
                            <Form.Group controlId="email" as={Row}>
                                <Form.Label column sm={3}>
                                    Email
                                </Form.Label>
                                <Col sm={9}>
                                    <Form.Control
                                        type="email"
                                        {...register("email", {
                                            required:
                                                "Email address  is required",
                                        })}
                                        aria-invalid={
                                            errors.email ? "true" : "false"
                                        }
                                    />
                                    {errors.email &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.email
                                                .message as string,
                                        })}
                                </Col>
                            </Form.Group>
                            <Form.Group controlId="phone" as={Row}>
                                <Form.Label column sm={3}>
                                    Phone
                                </Form.Label>
                                <Col sm={9}>
                                    <Form.Control
                                        type="text"
                                        {...register("phone")}
                                        aria-invalid={
                                            errors.phone ? "true" : "false"
                                        }
                                    />
                                    {errors.phone &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.phone
                                                .message as string,
                                        })}
                                </Col>
                            </Form.Group>
                            <Form.Group controlId="password" as={Row}>
                                <Form.Label column sm={3}>
                                    Password
                                </Form.Label>
                                <Col sm={9}>
                                    <Form.Control
                                        type="password"
                                        {...register("password")}
                                        aria-invalid={
                                            errors.password ? "true" : "false"
                                        }
                                    />
                                    {errors.password &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.password
                                                .message as string,
                                        })}
                                </Col>
                            </Form.Group>
                            <Form.Group controlId="confirmPassword" as={Row}>
                                <Form.Label column sm={3}>
                                    Confirm Password
                                </Form.Label>
                                <Col sm={9}>
                                    <Form.Control
                                        type="password"
                                        {...register("confirmPassword")}
                                        aria-invalid={
                                            errors.confirmPassword
                                                ? "true"
                                                : "false"
                                        }
                                    />
                                    {errors.confirmPassword &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.confirmPassword
                                                .message as string,
                                        })}
                                </Col>
                            </Form.Group>
                            <Row>
                                <Col>
                                    <Button variant="primary" type="submit">
                                        Update
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Profile;
